import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Send,
  Globe,
  Palette,
  Target,
  CheckCircle2,
  ArrowRight,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validation patterns
const patterns = {
  name: /^[a-zA-Z\s'-]{2,50}$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[\d\s\-+()]{7,20}$/,
  businessName: /^[a-zA-Z0-9\s&'.,\-]{2,100}$/,
  spamPatterns: /\b(viagra|cialis|casino|lottery|winner|click here|buy now|free money|act now)\b/i,
  urlPattern: /(https?:\/\/[^\s]+)/gi
};

const websiteTypes = [
  { id: 'business', label: 'Business Website', description: 'Professional site for your company' },
  { id: 'ecommerce', label: 'E-commerce Store', description: 'Sell products online' },
  { id: 'portfolio', label: 'Portfolio', description: 'Showcase your work' },
  { id: 'blog', label: 'Blog', description: 'Share content and articles' },
  { id: 'landing', label: 'Landing Page', description: 'Single page for campaigns' },
  { id: 'other', label: 'Other', description: 'Something else entirely' }
];

const budgetRanges = [
  { id: 'monthly', label: 'Pay Monthly', description: 'Flexible monthly payments' },
  { id: 'full', label: 'Full Purchase', description: 'One-time payment' },
  { id: 'unsure', label: 'Not Sure Yet', description: 'Need guidance' }
];

const timeframes = [
  { id: 'flexible', label: 'Flexible' },
  { id: 'asap', label: 'ASAP' },
  { id: '1-2weeks', label: '1-2 weeks' },
  { id: '1month', label: 'Within a month' }
];

const GetStartedPage = () => {
  const [searchParams] = useSearchParams();
  const budgetFromUrl = searchParams.get('budget');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    websiteType: 'business',
    budget: budgetFromUrl === 'full' ? 'full' : 'monthly',
    timeframe: 'flexible',
    currentWebsite: '',
    description: '',
    features: '',
    inspiration: '',
    companyUrl: '' // Honeypot field
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitTime] = useState(Date.now());

  useEffect(() => {
    document.title = 'Get Started | TryWeb - Start Your Website Project';
  }, []);

  // Update budget if URL param changes
  useEffect(() => {
    if (budgetFromUrl) {
      setFormData(prev => ({
        ...prev,
        budget: budgetFromUrl === 'full' ? 'full' : 'monthly'
      }));
    }
  }, [budgetFromUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, websiteType: type }));
  };

  const handleBudgetSelect = (budget) => {
    setFormData(prev => ({ ...prev, budget: budget }));
  };

  const handleTimeframeSelect = (timeframe) => {
    setFormData(prev => ({ ...prev, timeframe: timeframe }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Honeypot check
    if (formData.companyUrl) {
      return { bot: true };
    }
    
    // Time check - if submitted too fast (< 5 seconds for longer form)
    if (Date.now() - submitTime < 5000) {
      return { bot: true };
    }
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!patterns.name.test(formData.name.trim())) {
      newErrors.name = 'Please enter a valid name';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!patterns.email.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (optional)
    if (formData.phone && !patterns.phone.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Business name validation
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    } else if (!patterns.businessName.test(formData.businessName.trim())) {
      newErrors.businessName = 'Please enter a valid business name';
    }
    
    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Please tell us about your project';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Please provide more details (at least 20 characters)';
    } else if (formData.description.trim().length > 5000) {
      newErrors.description = 'Description is too long (max 5000 characters)';
    }
    
    // Spam check in text fields
    const textToCheck = `${formData.description} ${formData.features} ${formData.inspiration}`;
    if (patterns.spamPatterns.test(textToCheck)) {
      newErrors.description = 'Your submission contains blocked content';
    }
    
    // Check for excessive URLs
    const urlMatches = textToCheck.match(patterns.urlPattern);
    if (urlMatches && urlMatches.length > 5) {
      newErrors.description = 'Too many links in your submission';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    // Silent rejection for bots
    if (validationErrors.bot) {
      setSubmitted(true);
      return;
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Don't send honeypot field to backend
      const { companyUrl, ...submitData } = formData;
      await axios.post(`${API}/get-started`, submitData);
      setSubmitted(true);
      toast.success('Your request has been sent! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PageLayout>
        <section className="relative py-24 hero-glow min-h-[70vh] flex items-center">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Thank You!
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              We've received your website request and will get back to you within 24 hours. 
              Keep an eye on your inbox for our response!
            </p>
            <p className="text-muted-foreground">
              In the meantime, feel free to browse our <a href="/projects" className="text-primary hover:underline">portfolio</a> for inspiration.
            </p>
          </div>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative py-16 hero-glow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            <span className="text-gradient">Let's Build Your Website</span>
          </h1>
          <p className="text-xl text-muted-foreground">
            Tell us about your project and we'll create a stunning website tailored to your business. 
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-12">
            
            {/* Contact Details */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                Your Details
              </h2>
              
              {/* Honeypot field - hidden from users */}
              <div className="hidden" aria-hidden="true">
                <Label htmlFor="companyUrl">Company URL</Label>
                <Input 
                  id="companyUrl"
                  name="companyUrl"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.companyUrl}
                  onChange={handleChange}
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input 
                    id="name"
                    name="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'border-red-500' : ''}
                    data-testid="getstarted-name"
                  />
                  {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'border-red-500' : ''}
                    data-testid="getstarted-email"
                  />
                  {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+44 1234 567890"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'border-red-500' : ''}
                    data-testid="getstarted-phone"
                  />
                  {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessName">Business Name *</Label>
                  <Input 
                    id="businessName"
                    name="businessName"
                    placeholder="Your Business Ltd"
                    value={formData.businessName}
                    onChange={handleChange}
                    className={errors.businessName ? 'border-red-500' : ''}
                    data-testid="getstarted-business"
                  />
                  {errors.businessName && <p className="text-red-500 text-sm">{errors.businessName}</p>}
                </div>
              </div>
            </div>

            {/* Website Type */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-primary" />
                </div>
                What Type of Website Do You Need? *
              </h2>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {websiteTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeSelect(type.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.websiteType === type.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                        : 'border-white/10 hover:border-primary/30'
                    }`}
                    data-testid={`type-${type.id}`}
                  >
                    <p className="font-semibold">{type.label}</p>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Budget */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Palette className="w-5 h-5 text-primary" />
                </div>
                Preferred Budget
              </h2>
              
              <div className="grid sm:grid-cols-3 gap-4">
                {budgetRanges.map((budget) => (
                  <button
                    key={budget.id}
                    type="button"
                    onClick={() => handleBudgetSelect(budget.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.budget === budget.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                        : 'border-white/10 hover:border-primary/30'
                    }`}
                    data-testid={`budget-${budget.id}`}
                  >
                    <p className="font-semibold">{budget.label}</p>
                    <p className="text-xs text-muted-foreground">{budget.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                When Do You Need It?
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {timeframes.map((time) => (
                  <button
                    key={time.id}
                    type="button"
                    onClick={() => handleTimeframeSelect(time.id)}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      formData.timeframe === time.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                        : 'border-white/10 hover:border-primary/30'
                    }`}
                    data-testid={`timeframe-${time.id}`}
                  >
                    <p className="font-medium">{time.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-card/50 border border-white/5 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Tell Us More</h2>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="currentWebsite">Do You Have a Current Website? (Optional)</Label>
                  <Input 
                    id="currentWebsite"
                    name="currentWebsite"
                    placeholder="https://www.example.com"
                    value={formData.currentWebsite}
                    onChange={handleChange}
                    data-testid="getstarted-current"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Describe Your Business & What You Want *</Label>
                  <Textarea 
                    id="description"
                    name="description"
                    placeholder="Tell us about your business, your target audience, and what you want to achieve with your website..."
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className={errors.description ? 'border-red-500' : ''}
                    data-testid="getstarted-description"
                  />
                  {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="features">Any Specific Features You Need? (Optional)</Label>
                  <Textarea 
                    id="features"
                    name="features"
                    placeholder="E.g., contact form, booking system, gallery, blog, e-commerce, etc..."
                    rows={3}
                    value={formData.features}
                    onChange={handleChange}
                    data-testid="getstarted-features"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inspiration">Any Websites You Like for Inspiration? (Optional)</Label>
                  <Textarea 
                    id="inspiration"
                    name="inspiration"
                    placeholder="Share links to websites you admire or want yours to look like..."
                    rows={2}
                    value={formData.inspiration}
                    onChange={handleChange}
                    data-testid="getstarted-inspiration"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="text-center">
              <Button 
                type="submit" 
                size="lg" 
                className="glow-primary gap-2 px-12"
                disabled={loading}
                data-testid="getstarted-submit"
              >
                {loading ? 'Sending...' : 'Submit Your Request'}
                <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                We'll review your request and get back to you within 24 hours.
              </p>
            </div>
          </form>
        </div>
      </section>
    </PageLayout>
  );
};

export default GetStartedPage;
